import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, Sparkles, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { formatPrice } from '@/lib/helpers';
import { cn } from '@/lib/utils';
import { buildEventPath, isEventLive } from '../utils/eventUtils';
import { LiveBadge } from './LiveBadge';
import { FREE_PILL_COLOR_CLASS } from '../constants/badgeStyles';

const HOLD_TIME = 5000; // intervalo do autoplay (e pausa após interação manual)
const SLOT_SIZE = 'w-[230px] sm:w-[280px] lg:w-[320px] h-[350px] sm:h-[430px] lg:h-[495px]';

// Efeito de escala "Cover Flow": o card no snap ativo fica em destaque
// (escala 1, sem blur), os vizinhos encolhem e descem levemente conforme
// a distância (em nº de slides) até o ativo. Adaptado do exemplo oficial
// "Scale" da documentação do Embla — usa a progressão real do scroll
// (scrollProgress) em vez de recalcular posição por índice, então
// acompanha o drag do usuário quadro a quadro. De propósito só transform
// (scale + translateY) + opacity — nada de rotateY/perspective/inclinação:
// 3D é bem mais pesado pro navegador compor a cada frame de scroll, e há
// uma pegadinha conhecida de overflow-hidden não recortar direito
// elemento com transform 3D ativo.
const CENTER_SCALE = 1; // card ativo: escala 1.0, sem exagero
const SCALE_DROP = 0.175; // escala cai ~0.175 por slide de distância (~0.825 no vizinho)
const MIN_SCALE = 0.6;
const OPACITY_FACTOR = 2.4; // ~0.58 (58%) no vizinho imediato
const MIN_OPACITY = 0.3;
const TRANSLATE_Y_MAX = 20; // px que os vizinhos descem em relação ao card central

function GallerySkeleton() {
  return (
    <div className="flex items-center gap-3 py-4">
      <div className="w-[190px] sm:w-[230px] lg:w-[265px] h-[290px] sm:h-[355px] lg:h-[410px] mt-5 rounded-[28px] bg-muted animate-pulse" />
      <div className="w-[230px] sm:w-[280px] lg:w-[320px] h-[350px] sm:h-[430px] lg:h-[495px] rounded-[28px] bg-muted animate-pulse" />
      <div className="w-[190px] sm:w-[230px] lg:w-[265px] h-[290px] sm:h-[355px] lg:h-[410px] mt-5 rounded-[28px] bg-muted animate-pulse" />
    </div>
  );
}

function HeroCard({ event, isCenter, onActivate }) {
  const { id, title, dateLabel, location, isFree, ticketPrice, imageUrl, boosted } = event;
  const live = isEventLive(event._raw);

  const handleClick = (e) => {
    if (!isCenter) {
      e.preventDefault();
      onActivate();
    }
  };

  return (
    <Link
      to={buildEventPath(id, title)}
      onClick={handleClick}
      className={cn(
        'group relative block w-full h-full rounded-[28px] overflow-hidden bg-muted cursor-pointer',
        'ring-1 ring-black/5',
        isCenter ? 'shadow-2xl' : 'shadow-md hover:scale-[1.02]',
        'transition-[transform,box-shadow] duration-300 ease-out'
      )}
    >
      <img
        src={imageUrl}
        alt={title}
        draggable={false}
        className="w-full h-full object-cover select-none group-hover:scale-[1.02] transition-transform duration-300 ease-out"
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent" />

      {(live || boosted) && (
        <div className="absolute top-4 left-4 flex flex-col items-start gap-2">
          {live && <LiveBadge />}
          {boosted && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-black/25 text-white/80 backdrop-blur-sm">
              <Sparkles className="w-3 h-3" />
              Destaque
            </span>
          )}
        </div>
      )}

      <div className="absolute top-4 right-4">
        <span className={cn(
          'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium backdrop-blur-sm',
          isFree ? FREE_PILL_COLOR_CLASS : 'bg-white/85 text-slate-900'
        )}>
          {isFree ? 'Gratuito' : formatPrice(ticketPrice)}
        </span>
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-5">
        <h3 className="text-white font-bold text-lg md:text-xl leading-tight mb-2 line-clamp-2">
          {title}
        </h3>
        <div className="flex flex-col gap-1 text-white/70 text-xs">
          <div className="flex items-center gap-1.5">
            <Clock className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{dateLabel}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{location}</span>
          </div>
        </div>

        <div className="flex items-center gap-1 mt-2.5 text-white/90 text-xs font-medium opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 ease-out">
          Ver evento <ArrowRight className="w-3 h-3" />
        </div>
      </div>
    </Link>
  );
}

// Embla só ativa loop:true de fato ("canLoop") se houver slides o
// suficiente para preencher o viewport com clones dos dois lados; com
// poucos eventos em destaque (ex.: 3) essa checagem falha e o Embla
// desativa o loop silenciosamente (sem warning) — o carrossel então
// nunca centraliza de verdade, trava no último slide e perde o
// vizinho de um dos lados. Repetir os itens só para preencher os slides
// renderizados (mantendo os dots/cliques mapeados pro evento real via
// módulo) resolve sem tocar em dado nenhum.
const MIN_SLIDES_FOR_LOOP = 8;

export function HeroCarouselV2({ events, loading }) {
  const items = (events || []).filter((e) => e.imageUrl);
  const n = items.length;
  const repeat = n > 0 ? Math.max(1, Math.ceil(MIN_SLIDES_FOR_LOOP / n)) : 1;
  const slides = n > 1
    ? Array.from({ length: n * repeat }, (_, i) => ({ event: items[i % n], key: i }))
    : items.map((event) => ({ event, key: event.id }));

  const autoplayRef = useRef(
    Autoplay({ delay: HOLD_TIME, stopOnInteraction: false, stopOnMouseEnter: true })
  );
  const [emblaRef, emblaApi] = useEmblaCarousel(
    // duration em "quadros": ~27 quadros a 60fps ≈ 450ms, dentro da janela
    // 400–500ms pedida para a troca de slide (easing padrão do Embla já é suave)
    { loop: true, align: 'center', skipSnaps: false, duration: 27 },
    [autoplayRef.current]
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const tweenNodesRef = useRef([]);
  const tweenFactorRef = useRef(0);

  const setTweenNodes = useCallback((api) => {
    tweenNodesRef.current = api.slideNodes().map((slideNode) => slideNode.querySelector('[data-tween]'));
  }, []);

  const setTweenFactor = useCallback((api) => {
    tweenFactorRef.current = SCALE_DROP * api.scrollSnapList().length;
  }, []);

  // Aplica scale/opacity/z-index a cada slide com base na distância real
  // até o snap ativo — a mesma técnica usada nos exemplos oficiais do
  // Embla, robusta ao loop (trata o ponto de virada do laço) e ao drag
  // contínuo (roda a cada evento de scroll, não só ao trocar de slide).
  const tweenScale = useCallback((api, eventName) => {
    const engine = api.internalEngine();
    const scrollProgress = api.scrollProgress();
    const slidesInView = api.slidesInView();
    const isScrollEvent = eventName === 'scroll';

    api.scrollSnapList().forEach((scrollSnap, snapIndex) => {
      let diffToTarget = scrollSnap - scrollProgress;
      const slidesInSnap = engine.slideRegistry[snapIndex];

      slidesInSnap.forEach((slideIndex) => {
        if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

        if (engine.options.loop) {
          engine.slideLooper.loopPoints.forEach((loopItem) => {
            const target = loopItem.target();
            if (slideIndex === loopItem.index && target !== 0) {
              const sign = Math.sign(target);
              if (sign === -1) diffToTarget = scrollSnap - (1 + scrollProgress);
              if (sign === 1) diffToTarget = scrollSnap + (1 - scrollProgress);
            }
          });
        }

        const rawDelta = diffToTarget * tweenFactorRef.current;
        const distance = Math.abs(rawDelta);
        const scale = Math.max(CENTER_SCALE - distance, MIN_SCALE);
        const opacity = Math.max(1 - distance * OPACITY_FACTOR, MIN_OPACITY);
        // satura em 1 slide de distância: vizinho imediato já desce o
        // máximo (~20px), cards mais distantes não descem mais que isso
        const translateY = Math.min(distance / SCALE_DROP, 1) * TRANSLATE_Y_MAX;
        const node = tweenNodesRef.current[slideIndex];
        if (node) {
          node.style.transform = `scale(${scale}) translateY(${translateY}px)`;
          node.style.opacity = String(opacity);
          node.style.zIndex = scale > CENTER_SCALE - 0.1 ? '30' : scale > 0.7 ? '20' : '10';
        }
      });
    });
  }, []);

  useEffect(() => {
    if (!emblaApi) return undefined;

    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    const onInit = () => {
      setTweenNodes(emblaApi);
      setTweenFactor(emblaApi);
      tweenScale(emblaApi);
    };

    onInit();
    onSelect();

    emblaApi
      .on('reInit', onInit)
      .on('reInit', onSelect)
      .on('select', onSelect)
      .on('scroll', tweenScale)
      .on('slideFocus', tweenScale);

    return () => {
      emblaApi
        .off('reInit', onInit)
        .off('reInit', onSelect)
        .off('select', onSelect)
        .off('scroll', tweenScale)
        .off('slideFocus', tweenScale);
    };
  }, [emblaApi, setTweenNodes, setTweenFactor, tweenScale]);

  if (loading) return <GallerySkeleton />;
  if (n === 0) return null;

  return (
    // w-full: preenche exatamente a coluna que o grid do Hero reserva pra
    // ele (ver HomePage.jsx), a mesma largura útil das demais sections.
    // Antes havia um max-w fixo em px aqui, pensado pra não deixar o
    // Embla (align: 'center') centralizar o card ativo num espaço grande
    // demais — mas por ser um valor fixo, sobrava vão vazio à direita em
    // telas largas (a coluna do grid é bem maior que o cap em >~1600px).
    // Como a coluna do grid agora já é proporcional ao container (não
    // esticada livre até a borda da tela), o wrapper pode ocupar 100% dela
    // sem reabrir aquele vão.
    <div className="w-full min-w-0">
      {/* "Sangria": o Embla precisa de um overflow-hidden pra recortar o
          scroll horizontal, mas isso também cortava a box-shadow dos cards
          (shadow-2xl no ativo) bem em cima da borda. Em vez de aplicar o
          overflow-hidden direto no viewport do Embla (o que mudaria o
          tamanho que ele mede via ref e alteraria os cálculos de
          centro/loop), ele fica num wrapper por fora, maior — com padding
          compensado por uma margin negativa idêntica. padding e margin se
          cancelam exatamente (mesma lógica do cálculo de largura de um
          bloco width:auto), então o viewport do Embla (a div com o ref)
          continua com o tamanho e posição de sempre; só ganha uma faixa
          extra ao redor, dentro do próprio recorte, onde a sombra pode
          respirar antes de ser cortada. */}
      <div className="relative overflow-hidden -mx-10 -mt-8 -mb-16">
        <div className="px-10 pt-8 pb-16">
          <div className="group/gallery relative" ref={emblaRef}>
            {/* py-4: reserva respiro vertical pro crescimento do card central
                (scale) não esbarrar no recorte da sangria — sem essa folga, o
                topo/base do card (e a curva do border-radius) ficam cortados
                rente à altura do viewport. Não afeta a largura, que continua
                sendo recortada de propósito pelo fade lateral. */}
            <div className="flex items-center gap-3 py-4">
              {slides.map(({ event, key }, i) => (
                <div key={key} className="shrink-0" style={{ flex: '0 0 auto' }}>
                  <div
                    data-tween
                    className={cn('origin-center will-change-[transform,opacity]', SLOT_SIZE)}
                  >
                    <HeroCard
                      event={event}
                      isCenter={i === selectedIndex}
                      onActivate={() => emblaApi?.scrollTo(i)}
                    />
                  </div>
                </div>
              ))}
            </div>

            {n > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => emblaApi?.scrollPrev()}
                  aria-label="Evento anterior"
                  className={cn(
                    'hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 z-40 items-center justify-center',
                    'w-9 h-9 rounded-full bg-white/90 text-slate-900 shadow-lg backdrop-blur-sm',
                    'opacity-0 group-hover/gallery:opacity-100 transition-all duration-300 ease-out',
                    'hover:bg-white hover:scale-105 active:scale-95'
                  )}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={() => emblaApi?.scrollNext()}
                  aria-label="Próximo evento"
                  className={cn(
                    'hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 z-40 items-center justify-center',
                    'w-9 h-9 rounded-full bg-white/90 text-slate-900 shadow-lg backdrop-blur-sm',
                    'opacity-0 group-hover/gallery:opacity-100 transition-all duration-300 ease-out',
                    'hover:bg-white hover:scale-105 active:scale-95'
                  )}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Fade nas bordas — vive no wrapper de sangria (o recorte real,
            overflow-hidden), não no viewport do Embla (ref), porque a área
            visível agora é mais larga que ele (ver comentário acima). Largura
            = fade original (64px) + sangria lateral (px-10 = 40px), ancorada
            na borda verdadeira: o trecho extra cobre a faixa antes invisível
            (agora exposta pra sombra respirar) e o ponto onde vira 100%
            transparente cai exatamente onde caía antes — sem isso, a troca de
            slides expõe um traço nítido entre o fim do fade antigo e a nova
            borda de recorte. */}
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-[104px] z-[35] bg-gradient-to-r from-background/70 to-transparent" />
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-[104px] z-[35] bg-gradient-to-l from-background/70 to-transparent" />
      </div>

      {n > 1 && (
        <div className="flex items-center justify-center gap-2 mt-5">
          {items.map((event, i) => {
            const isActive = selectedIndex % n === i;
            return (
              <button
                key={event.id}
                type="button"
                onClick={() => {
                  // vários slides do array repetido apontam pro mesmo evento
                  // real (i) — pula pro mais próximo do índice atual, assim o
                  // loop do Embla sempre anima pelo caminho mais curto
                  let closest = i;
                  let bestDist = Infinity;
                  for (let idx = i; idx < slides.length; idx += n) {
                    const dist = Math.abs(idx - selectedIndex);
                    if (dist < bestDist) {
                      bestDist = dist;
                      closest = idx;
                    }
                  }
                  emblaApi?.scrollTo(closest);
                }}
                aria-label={`Ir para o evento ${i + 1}`}
                aria-current={isActive}
                className={cn(
                  'h-1.5 rounded-full transition-all duration-300 ease-out',
                  isActive ? 'w-6 bg-foreground' : 'w-1.5 bg-foreground/25 hover:bg-foreground/40'
                )}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
